// Welke pagina's er zijn, wat ze vereisen en hoe ze in de adresbalk staan.
// Eén declaratie; menu, titel, toegang en herstel na verversen lezen hieruit.
//
// Dit is gemak, geen slot. De database weigert wat een rol niet mag; de router
// voorkomt alleen dat iemand op een pagina belandt die toch leeg of kapot is.
// Titels komen uit de vertalingen (titel.<naam>).
import { t } from './lib/i18n.js';

// Elke pagina hoort bij één module. Een module is een eigen werkruimte met
// een eigen menu: wie in Leden werkt, ziet geen toernooimenu, en andersom.
// Toernooien zijn bovendien een module die per organisatie aan of uit staat.
const ORG = [
  { naam: 'toernooien', module: 'toernooien', vereist: 'data.read', entitlement: 'toernooien' },
  { naam: 'dashboard', module: 'toernooien', vereist: 'data.read', entitlement: 'toernooien' },
  { naam: 'teams',     module: 'toernooien', vereist: 'data.read', entitlement: 'toernooien' },
  { naam: 'activity',  module: 'toernooien', vereist: 'data.read', entitlement: 'toernooien' },
  { naam: 'flagged',   module: 'toernooien', vereist: 'data.read', entitlement: 'toernooien' },
  { naam: 'clubs',     module: 'toernooien', vereist: 'data.read', entitlement: 'toernooien' },
  { naam: 'deelnemers', module: 'toernooien', vereist: 'data.read', entitlement: 'toernooien' },
  { naam: 'sporters',  module: 'toernooien', vereist: 'data.read', entitlement: 'toernooien' },
  { naam: 'templates', module: 'toernooien', vereist: 'data.read', entitlement: 'toernooien' },
  { naam: 'archived',  module: 'toernooien', vereist: 'data.read', entitlement: 'toernooien' },
  { naam: 'toernooiinstellingen', module: 'toernooien', vereist: 'settings.manage', entitlement: 'toernooien' },
  { naam: 'activiteiten', module: 'activiteiten', vereist: 'data.read', entitlement: 'activiteiten' },
  { naam: 'leden',     module: 'leden', vereist: 'member.read', entitlement: 'leden' },
  { naam: 'clubregister', module: 'leden', vereist: 'member.read', entitlement: 'bond' },
  { naam: 'aansluitingen', module: 'leden', vereist: 'member.read', entitlement: 'leden' },
  { naam: 'verzoeken', module: 'leden', vereist: 'case.read', entitlement: 'leden' },
  { naam: 'goedkeuringen', module: 'goedkeuringen', vereist: 'approval.decide' },
  { naam: 'admins',    module: 'beheer', vereist: 'users.manage' },
  { naam: 'settings',  module: 'beheer', vereist: 'settings.manage' },
].map(r => ({ entitlement: null, ...r }));

// Volgorde waarin modules in de kiezer staan, en de standaardmodule.
export const MODULES = ['toernooien', 'leden', 'activiteiten', 'goedkeuringen', 'beheer'];
let huidigeModule = null;

const PLATFORM = ['dash', 'orgs', 'terms', 'consent', 'email', 'stripe', 'instellingen'].map(naam => ({ naam }));

const SYSTEEMROLLEN = (() => {
  const viewer = ['data.read', 'member.read', 'finance.read'];
  const editor = [...viewer, 'data.write', 'member.write', 'finance.write', 'activity.manage', 'registration.manage'];
  const admin = [...editor, 'data.delete', 'member.delete', 'person.export', 'data.export', 'finance.writeoff',
                 'approval.decide', 'api.token.manage', 'users.manage', 'settings.manage',
                 'grade.set', 'licence.grant', 'member.suspend', 'club.manage', 'case.read', 'case.write'];
  const owner = [...admin, 'org.manage', 'role.manage', 'passport.manage'];
  return { viewer, editor, admin, owner };
})();

let permissies = new Set();
let entitlements = null; // null: nog niet bekend, alles aan

// opties.bekijkAls: toon precies wat deze systeemrol ziet (platformbeheer,
// 'bekijk als'). Dan geen opvraag van de echte rechten.
export async function laadPermissies(sb, orgId, rol, opties = {}) {
  permissies = new Set(SYSTEEMROLLEN[rol] || SYSTEEMROLLEN.viewer);
  if (opties.bekijkAls) return;
  if (!sb || !orgId) return;
  const { data, error } = await sb.from('my_permissions').select('permission_key').eq('organisation_id', orgId);
  if (!error && data?.length) permissies = new Set(data.map(r => r.permission_key));
}
export const heeft = perm => permissies.has(perm);
export const allePermissies = () => [...permissies].sort();
export const systeemrollen = () => Object.keys(SYSTEEMROLLEN);
export function zetEntitlements(set) { entitlements = set ? new Set(set) : null; }

// Welke modules deze organisatie heeft: de standaard van haar type, met de
// uitzonderingen uit entitlements. Bestaat de ledenlaag nog niet in de
// database (migratie niet gedraaid), dan blijft alles aan.
export async function laadEntitlements(sb, orgId) {
  entitlements = null;
  if (!sb || !orgId) return;
  const [org, features, eigen] = await Promise.all([
    sb.from('organisations').select('types').eq('id', orgId).maybeSingle(),
    sb.from('features').select('key, standaard_voor'),
    sb.from('entitlements').select('feature_key, enabled').eq('organisation_id', orgId),
  ]);
  if (org.error || features.error || eigen.error || !org.data?.types) return;
  const aan = new Set(features.data.filter(f => f.standaard_voor.some(tp => org.data.types.includes(tp))).map(f => f.key));
  for (const e of eigen.data) { if (e.enabled) aan.add(e.feature_key); else aan.delete(e.feature_key); }
  entitlements = aan;
}
export const heeftModule = key => !entitlements || entitlements.has(key);
export const modules = () => (entitlements ? [...entitlements].sort() : null);

const mag = r => heeft(r.vereist) && (!r.entitlement || !entitlements || entitlements.has(r.entitlement));
export const orgRoute = naam => ORG.find(r => r.naam === naam) || null;
export const moduleVan = naam => orgRoute(naam)?.module || null;
// Modules waar deze gebruiker bij deze organisatie in kan.
export const beschikbareModules = () => MODULES.filter(m => ORG.some(r => r.module === m && mag(r)));
export function zetModule(m) { huidigeModule = m; }
export const module = () => huidigeModule;
// Waar je binnenkomt: de eerste pagina van de huidige module die mag, anders
// de eerste pagina van de eerste module die er is.
export const eerstePagina = (m = huidigeModule) =>
  (ORG.find(r => r.module === m && mag(r)) || ORG.find(r => r.module === beschikbareModules()[0] && mag(r)) || ORG[0]).naam;
export const magNaar = naam => { const r = orgRoute(naam); return !!r && mag(r); };
export const titel = naam => {
  const tekst = t('titel.' + naam);
  return tekst === 'titel.' + naam ? naam : tekst;
};
export const platformRoute = naam => PLATFORM.some(r => r.naam === naam);

// Verbergt menu-items van pagina's die deze gebruiker niet mag openen.
// Alleen het menu van de huidige module; binnen die module alleen wat mag. Een
// kopje zonder zichtbare items eronder verdwijnt ook.
export function bouwMenu() {
  const nav = document.getElementById('navOrg');
  if (!nav) return;
  for (const el of nav.querySelectorAll('[data-module]')) {
    const r = el.dataset.page ? orgRoute(el.dataset.page) : null;
    el.hidden = (huidigeModule && el.dataset.module !== huidigeModule) || (r ? !mag(r) : false);
  }
  let kop = null; let items = 0;
  const sluit = () => { if (kop) kop.hidden = kop.hidden || items === 0; };
  for (const el of nav.children) {
    if (el.classList.contains('nav-section-label')) { sluit(); kop = el; items = 0; }
    else if (el.classList.contains('nav-item') && !el.hidden) items++;
  }
  sluit();
}

// Adressen in de adresbalk zijn Engels; de namen in de code blijven zoals ze
// zijn. Alleen wat afwijkt staat hier. Oude Nederlandse adressen (bladwijzers,
// gedeelde links) werken nog: leesAdres vertaalt ze terug.
const ADRES_ORG = {
  toernooien: 'tournaments',
  toernooiinstellingen: 'tournament-settings',
  activiteiten: 'activities',
  leden: 'members',
  clubregister: 'clubs-register',
  aansluitingen: 'affiliations',
  verzoeken: 'requests',
  goedkeuringen: 'approvals',
  activity: 'activity-log',
  admins: 'users',
  deelnemers: 'competitors',
  sporters: 'athletes',
};
const ADRES_PLATFORM = { instellingen: 'settings' };
const terug = kaart => Object.fromEntries(Object.entries(kaart).map(([naam, adres]) => [adres, naam]));
const NAAM_ORG = terug(ADRES_ORG);
const NAAM_PLATFORM = terug(ADRES_PLATFORM);

// Interne naam ('leden', 'platform-instellingen') naar adres ('members', 'platform-settings').
export function adres(naam = '') {
  if (naam.startsWith('platform-')) {
    const p = naam.slice('platform-'.length);
    return 'platform-' + (ADRES_PLATFORM[p] || p);
  }
  return ADRES_ORG[naam] || naam;
}

// Na het inloggen met Google staat er kort een token achter het hekje. Dat
// matcht met geen enkele route en wordt dus genegeerd.
export function leesAdres() {
  const hash = decodeURIComponent(location.hash.slice(1));
  if (hash.startsWith('platform-')) {
    const deel = hash.slice('platform-'.length);
    const naam = NAAM_PLATFORM[deel] || deel;
    return platformRoute(naam) ? { soort: 'platform', naam } : null;
  }
  const naam = NAAM_ORG[hash] || hash;
  return orgRoute(naam) ? { soort: 'org', naam } : null;
}

// Een nieuwe pagina is een nieuwe stap in de geschiedenis, zodat de terugknop
// werkt. Bij binnenkomen of herstellen vervangt hij de huidige stap.
export function onthoud(waarde, { vervang = false, staat = {} } = {}) {
  const doel = adres(waarde);
  const url = `${location.pathname}${location.search}#${doel}`;
  const st = { ...(history.state || {}), ...staat, tornema: true };
  if (vervang || location.hash.slice(1) === doel) history.replaceState(st, '', url);
  else history.pushState(st, '', url);
}
